import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CreditCard, Copy, Clock, Gift } from 'lucide-react';
import { toast } from 'sonner';

export default function CouponsPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [couponCode, setCouponCode] = useState('');

  // Mock coupons data
  const mockCoupons = [
    {
      id: 1,
      code: 'WELCOME50',
      title: '50 TL İndirim Kuponu',
      description: '200 TL ve üzeri alışverişlerde geçerlidir',
      discount: 50,
      minAmount: 200,
      expiryDate: '2025-02-28',
      isActive: true,
      category: 'Genel',
    },
    {
      id: 2,
      code: 'FASHION20',
      title: '%20 İndirim',
      description: 'Moda kategorisinde geçerlidir',
      discount: 20,
      minAmount: 150,
      expiryDate: '2025-02-15',
      isActive: true,
      category: 'Moda',
      isPercentage: true,
    },
    {
      id: 3,
      code: 'OLDCOUPON',
      title: '100 TL İndirim',
      description: 'Süresi dolmuş kupon',
      discount: 100,
      minAmount: 500,
      expiryDate: '2025-01-15',
      isActive: false,
      category: 'Genel',
    },
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
        <div className="bg-white rounded-xl p-12 text-center max-w-md">
          <CreditCard className="w-24 h-24 mx-auto text-gray-300 mb-6" />
          <h2 className="font-manrope font-bold text-2xl mb-4">Giriş Yapın</h2>
          <p className="text-gray-600 mb-8">Kuponlarınızı görüntülemek için giriş yapmanız gerekiyor.</p>
          <Link
            to="/giris"
            className="inline-block bg-trendyol-orange hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
          >
            Giriş Yap
          </Link>
        </div>
      </div>
    );
  }

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Kupon kodu kopyalandı!');
  };

  const activeCoupons = mockCoupons.filter((c) => c.isActive);
  const expiredCoupons = mockCoupons.filter((c) => !c.isActive);

  return (
    <div className="min-h-screen bg-gray-50 py-8 md:py-12" data-testid="coupons-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-manrope font-bold text-2xl md:text-3xl">Kuponlarım</h1>
          <Link
            to="/hesabim"
            className="text-trendyol-orange hover:underline font-medium"
          >
            Hesabıma Dön
          </Link>
        </div>

        {/* Add Coupon Code */}
        <div className="bg-gradient-to-r from-trendyol-orange to-orange-600 rounded-xl p-6 mb-8 text-white">
          <h2 className="font-semibold text-xl mb-3">Kupon Kodunuz Var Mı?</h2>
          <p className="mb-4 text-orange-100">Kupon kodunuzu girerek hesabınıza ekleyin</p>
          <div className="flex gap-3">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder="Kupon kodunu girin"
              className="flex-1 px-4 py-3 rounded-lg text-gray-900 focus:outline-none"
              data-testid="coupon-code-input"
            />
            <button
              onClick={() => {
                if (couponCode) {
                  toast.success('Kupon hesabınıza eklendi!');
                  setCouponCode('');
                }
              }}
              className="bg-white text-trendyol-orange font-semibold px-8 py-3 rounded-lg hover:bg-orange-50 transition-colors"
              data-testid="add-coupon-button"
            >
              Ekle
            </button>
          </div>
        </div>

        {/* Active Coupons */}
        <div className="mb-8">
          <h2 className="font-semibold text-xl mb-4">Aktif Kuponlar ({activeCoupons.length})</h2>
          {activeCoupons.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <Gift className="w-24 h-24 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600">Henüz aktif kuponunuz yok</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {activeCoupons.map((coupon) => (
                <div
                  key={coupon.id}
                  className="bg-white rounded-xl overflow-hidden border-2 border-gray-200 hover:border-trendyol-orange transition-colors"
                  data-testid={`coupon-${coupon.id}`}
                >
                  <div className="bg-gradient-to-r from-trendyol-orange to-orange-600 p-4 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-2xl">
                        {coupon.isPercentage ? `%${coupon.discount}` : `${coupon.discount} TL`}
                      </h3>
                      <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                        {coupon.category}
                      </span>
                    </div>
                    <p className="text-lg font-semibold">{coupon.title}</p>
                  </div>

                  <div className="p-4">
                    <p className="text-sm text-gray-600 mb-3">{coupon.description}</p>
                    <p className="text-sm text-gray-600 mb-4">
                      Minimum: {coupon.minAmount} TL
                    </p>

                    <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>
                        Son kullanma: {new Date(coupon.expiryDate).toLocaleDateString('tr-TR')}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <div className="flex-1 bg-gray-100 px-4 py-2 rounded-lg font-mono font-bold text-center">
                        {coupon.code}
                      </div>
                      <button
                        onClick={() => handleCopyCode(coupon.code)}
                        className="px-4 py-2 bg-trendyol-orange hover:bg-orange-600 text-white rounded-lg transition-colors"
                        data-testid={`copy-coupon-${coupon.id}`}
                      >
                        <Copy className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Expired Coupons */}
        {expiredCoupons.length > 0 && (
          <div>
            <h2 className="font-semibold text-xl mb-4">Süresi Dolan Kuponlar</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {expiredCoupons.map((coupon) => (
                <div
                  key={coupon.id}
                  className="bg-white rounded-xl overflow-hidden border-2 border-gray-200 opacity-60"
                  data-testid={`expired-coupon-${coupon.id}`}
                >
                  <div className="bg-gray-400 p-4 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-2xl">
                        {coupon.isPercentage ? `%${coupon.discount}` : `${coupon.discount} TL`}
                      </h3>
                      <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                        {coupon.category}
                      </span>
                    </div>
                    <p className="text-lg font-semibold">{coupon.title}</p>
                  </div>

                  <div className="p-4">
                    <p className="text-sm text-gray-600 mb-3">{coupon.description}</p>
                    <p className="text-sm text-red-600 font-medium">
                      Süresi {new Date(coupon.expiryDate).toLocaleDateString('tr-TR')} tarihinde doldu
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}